import { Fragment } from 'react'
// ** React Select Theme Colors
export const selectThemeColors = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: '#7367f01a', // for option hover bg-color
    primary: '#7367f0', // for selected option bg-color
    neutral10: '#7367f0', // for tags bg-color
    neutral20: '#ededed', // for input border-color
    neutral30: '#ededed' // for input hover border-color
  }
})

export const mapQtyToColor = (qtyOnHand, qtyNeeded) => {
  if (qtyOnHand >= qtyNeeded) {
    return null
  } else if (qtyOnHand > 0 && qtyOnHand < qtyNeeded) {
    return '#E67327'
  } else {
    return '#c93e1e'
  }
}

export const arrayToReactSelect = array => {
  return array.map(val => ({ value: val, label: val }))
}

export const isProductNeedsConfiguration = product => {
  if (!product) return false
  return product.is_configurable && !product.is_configured
}

export const selectPortalFields = portals => {
  const newPortals = portals.map(portal => {
    return {
      id: portal.id,
      name: portal.name
    }
  })
  return [{ id: -1, name: 'All Portals' }].concat(newPortals)
}

export const saveUrlToLocalStorage = url => {
  localStorage.setItem('redirectUrl', url ?? window.location.pathname)
}

/**
 * Utility function for localStorage actions.
 *
 * @param {'local' | 'session'} storageType - The type of storage to use.
 * @param {'get' | 'set' | 'remove' | 'clear'} action - The action to perform on localStorage.
 * @returns {(function(string, any=): any) | undefined} - The corresponding function to handle the specified action, or undefined if the action is not recognized.
 */
export const browserStorageUtility = (storageType = 'local', action = 'get') => {
  const browserStore = storageType === 'local' ? window.localStorage : window.sessionStorage

  const returnedActions = {
    /**
     * Get an item from browser storage.
     * @param {string} key - The key of the item to retrieve.
     * @returns {any} - The parsed value of the item in browser storage.
     */
    get: key => JSON.parse(browserStore.getItem(key)),

    /**
     * Set an item in browser storage.
     * @param {string} key - The key of the item to set.
     * @param {any} value - The value of the item to set.
     */
    set: (key, value) => {
      browserStore.setItem(key, JSON.stringify(value))
    },

    /**
     * Remove an item from browser storage.
     * @param {string} key - The key of the item to remove.
     */
    remove: key => {
      browserStore.removeItem(key)
    },

    /**
     * Clear all items in browser storage.
     */
    clear: () => {
      browserStore.clear()
    }
  }

  return returnedActions[action]
}

/**
 * shared function for amount_dollar columns on customer screens
 *
 * @param {string} filterValue - the search text as entered by the user
 * @param {string} selectedFilterOperation - one of the filter operations supported by the Devex grid
 * @returns {Array<string>} - returns the filter expressions as specified by Devex
 */
export const amountDollarFilter = (filterValue, selectedFilterOperation) => {
  if (['<>', '<', '>', '<=', '>='].includes(selectedFilterOperation)) {
    return ['amount_dollars', selectedFilterOperation, filterValue]
  }

  if (Number(filterValue) < 0) return [['amount_dollars', '=', filterValue]]

  return [['amount_dollars', '=', filterValue], 'or', ['amount_dollars', '=', -filterValue]]
}

export const isValidUrl = (url, secure = false) => {
  try {
    const urlObject = new URL(url)

    // Check protocol
    if (secure && urlObject.protocol !== 'https:') {
      return false
    }
    if (!secure && !['http:', 'https:'].includes(urlObject.protocol)) {
      return false
    }

    // Check if hostname has at least two parts (e.g., example.com)
    const hostnameParts = urlObject.hostname.split('.')
    if (hostnameParts.length < 2) {
      return false
    }

    // Check if hostname parts are non-empty
    if (hostnameParts.some(part => part.length === 0)) {
      return false
    }

    // Check TLD is at least 2 characters
    const tld = hostnameParts[hostnameParts.length - 1]
    if (tld.length < 2) {
      return false
    }

    return true
  } catch {
    return false
  }
}
export const validators = {
  email: (value, required = true) => {
    // 1. Ensure that a value is provided.
    if (required && !value) return 'Email address is required.'
    if (!required && !value) return true

    // 2. Check that the email contains exactly one '@' symbol.
    const atCount = value.split('@').length - 1
    if (atCount !== 1) return "Email must have one '@'."

    // 3. Split the email into local and domain parts.
    const [localPart, domainPart] = value.split('@')

    // 4. Validate the local part's length (must be between 1 and 64 characters).
    if (localPart.length < 1 || localPart.length > 64) return "Local part (before '@') must be between 1-64 characters."

    // 5. Validate the domain part's length (must be between 1 and 255 characters).
    if (domainPart.length < 1 || domainPart.length > 255)
      return "Domain part (after '@') must be between 1-255 characters."

    // 6. Ensure the local part does not start with a period.
    if (localPart.startsWith('.')) return "Local part (before '@') must not start with a period."

    // 7. Ensure the local part does not end with a period.
    if (localPart.endsWith('.')) return "Local part (before '@') must not end with a period."

    // 8. Check that the local part contains only allowed characters.
    // Allowed characters: letters, digits, and these special characters: !#$%&'*+/=?^_{|}~.-
    // (Note: period is allowed as a separator, and other disallowed characters will trigger an error)
    if (/[^a-zA-Z0-9!#$%&'*+/=?^_{|}~.-]/.test(localPart))
      return "Local part (before '@') contains invalid characters. Allowed: letters, digits, !#$%&’*+/=?^_{|}~- (periods as separators)."

    // 9. Ensure the domain part contains at least one period to separate labels and the TLD.
    if (!domainPart.includes('.'))
      return "Domain part (after '@') must contain at least one period separating labels and TLD."

    // 10. Split the domain part into individual labels using the period as a separator.
    const labels = domainPart.split('.')

    // 11. Check that none of the domain labels are empty (which could be caused by extra periods).
    if (labels.some(label => label.length === 0)) return "Domain (after '@') is incomplete."

    // 12. Validate each domain label except for the TLD:
    // - They must only contain letters, digits, and hyphens.
    // - They must not start or end with a hyphen.
    for (let i = 0; i < labels.length - 1; i++) {
      const label = labels[i]
      if (!/^[a-zA-Z0-9-]+$/.test(label))
        return 'Domain part (after ‘@’) can only contain letters, digits, and hyphens.'
      if (label.startsWith('-') || label.endsWith('-'))
        return "Domain part (after '@') labels must not start or end with a hyphen."
    }

    // 13. Validate the top-level domain (TLD), which is the last label:
    // - It must be between 2 and 63 characters long.
    // - It must contain only letters.
    const tld = labels[labels.length - 1]
    if (tld.length < 2 || tld.length > 63) return 'Top-level domain (e.g., .com) must be between 2-63 characters.'
    if (!/^[a-zA-Z]+$/.test(tld)) return 'Top-level domain (e.g., .com) must contain only letters.'

    // 14. Ensure the entire email does not contain consecutive periods.
    if (/\.\./.test(value)) return 'Email must not contain consecutive periods.'

    // 15. As a final catch-all, test the entire email string with a comprehensive regex.
    // This regex combines all previous validations and acts as a safety net.
    const catchAll =
      /^(?!.*\.\.)(?=^[^@]{1,64}@[^@]{1,255}$)[a-zA-Z0-9!#$%&'*+/=?^_{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,63}$/
    if (!catchAll.test(value)) return 'Invalid email address'

    // 16. If all validations pass, return true to indicate a valid email.
    return true
  },
  password: value => {
    // 1. Ensure that a password is provided.
    if (!value) return 'Password is required.'

    // 2. Check the password length (must be at least 8 characters).
    if (value.length < 15) return 'Password must be at least 15 characters long.'

    // 2. Check the password length (must be no more than 30 characters).
    if (value.length > 30) return 'Password cannot exceed 30 characters.'

    // 3. Check that the password contains at least one lowercase letter.
    if (!/[a-z]/.test(value)) return 'Password must contain at least one lowercase letter.'

    // 4. Check that the password contains at least one uppercase letter.
    if (!/[A-Z]/.test(value)) return 'Password must contain at least one uppercase letter.'

    // 5. Check that the password contains at least one digit.
    if (!/[0-9]/.test(value)) return 'Password must contain at least one digit.'

    // 6. Check that the password contains at least one special character.
    // Allowed special characters: # ? ! @ $ % ^ & * _ -
    if (!/[#?!@$%^&*_-]/.test(value)) return 'Password must contain at least one special character: #?!@$%^&*_-'

    // If all validations pass, return true to indicate a valid password.
    return true
  },
  multiselect: (value, message = 'At least one option must be selected') => {
    if (!value || value.length === 0) return message
    return true
  },
  url: value => {
    // 1. If no value provided, consider it valid (no required check).
    if (!value) return true

    // 2. Check that the URL starts with http:// or https://
    if (!value.startsWith('http://') && !value.startsWith('https://')) {
      return 'URL must start with http:// or https://'
    }

    // 3. Extract the protocol and the rest of the URL
    const protocol = value.startsWith('https://') ? 'https://' : 'http://'
    const restOfUrl = value.substring(protocol.length)

    // 4. Check if URL has www. subdomain (optional)
    const hasWww = restOfUrl.startsWith('www.')
    const withoutWww = hasWww ? restOfUrl.substring(4) : restOfUrl

    // 5. Split the URL into domain and path parts
    const [domainPart, ...pathParts] = withoutWww.split('/')
    const path = pathParts.length > 0 ? `/${pathParts.join('/')}` : ''

    // 6. Validate that domain part exists and is not empty
    if (!domainPart || domainPart.length === 0) {
      return 'URL must contain a domain name'
    }

    // 7. Check that domain contains a period (for TLD)
    if (!domainPart.includes('.')) {
      return 'Domain must contain a top-level domain (e.g., .com)'
    }

    // 8. Split domain into name and TLD
    const lastDotIndex = domainPart.lastIndexOf('.')
    const domainName = domainPart.substring(0, lastDotIndex)
    const tld = domainPart.substring(lastDotIndex + 1)

    // 9. Validate domain name length (1-256 characters)
    if (domainName.length < 1 || domainName.length > 256) {
      return 'Domain name must be between 1-256 characters'
    }

    // 10. Validate domain name characters
    if (!/^[-a-zA-Z0-9@:%._\+~#=]+$/.test(domainName)) {
      return 'Domain name contains invalid characters. Allowed: letters, digits, and -@:%._+~#='
    }

    // 11. Validate TLD length (1-6 characters)
    if (tld.length < 1 || tld.length > 6) {
      return 'Top-level domain must be between 1-6 characters'
    }

    // 12. Validate TLD characters (letters and numbers only)
    if (!/^[a-zA-Z0-9()]+$/.test(tld)) {
      return 'Top-level domain can only contain letters, numbers, and parentheses'
    }

    // 13. Validate path characters if path exists
    if (path && !/^[-a-zA-Z0-9()@:%_\+.~#?&\/=]*$/.test(path)) {
      return 'URL path contains invalid characters'
    }

    // 14. Check for consecutive periods in domain
    if (domainPart.includes('..')) {
      return 'Domain cannot contain consecutive periods'
    }

    // 15. Ensure domain doesn't start or end with a period
    if (domainPart.startsWith('.') || domainPart.endsWith('.')) {
      return 'Domain cannot start or end with a period'
    }

    // 16. As a final validation, test with the comprehensive regex
    const urlRegex =
      /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/
    if (!urlRegex.test(value)) {
      return 'Invalid URL format'
    }

    // 17. If all validations pass, return true to indicate a valid URL
    return true
  }
}

export * from './formatters'
/**
 * Formats an address object into JSX with line breaks
 *
 * @param {Object} address - The address object to format
 * @param {string} address.company_name - Company name
 * @param {string} address.poc_name - Point of contact name
 * @param {string} address.address_line_1 - First line of address
 * @param {string} address.address_line_2 - Second line of address (optional)
 * @param {string} address.city - City
 * @param {string} address.region - Region/State (alternative: address.state)
 * @param {string} address.zip_code - ZIP/Postal code (alternative: address.zip)
 * @param {string} address.country_code - Country code
 * @returns {JSX.Element|string} Formatted address as JSX with line breaks or empty string
 */
export const formatAddress = address => {
  if (!address) return ''

  const { company_name, poc_name, address_line_1, address_line_2, city, state, region, zip, zip_code, country_code } =
    address

  const stateOrRegion = state || region
  const zipCode = zip || zip_code
  let cityStateZip = city

  if (stateOrRegion && stateOrRegion.trim() !== '') {
    cityStateZip += `, ${stateOrRegion}`
  }

  if (zipCode && zipCode.trim() !== '') {
    cityStateZip += ` ${zipCode}`
  }

  const addressParts = [company_name, poc_name, address_line_1, address_line_2, cityStateZip, country_code].filter(
    part => part && part.trim() !== ''
  )

  return (
    <>
      {addressParts.map((part, index) => (
        <Fragment key={index}>
          {part}
          {index < addressParts.length - 1 && <br />}
        </Fragment>
      ))}
    </>
  )
}
